<template>
    <div class="blog">
        <PageContentLoader v-if="(stale || fetching) && !posts" />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :loading="fetching"
                @click="$emit('try-again')"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <TransitionListGrid>
            <div
                v-if="posts"
                :key="posts[0]?.id"
                :class="classBlogContent"
                class="blog-content"
            >
                <BlogHeader
                    class="blog-header"
                    :breadcrumbs
                    :categories
                    :title
                />

                <div
                    v-if="posts"
                    class="blog-grid-container"
                >
                    <BlogGrid
                        :get-page-link
                        :page
                        :posts
                        :limit
                        :total-count="totalPosts ?? 0"
                    />

                    <slot name="blocks"></slot>
                </div>

                <PageErrorFourHundredFour v-else />
            </div>
        </TransitionListGrid>

        <PageErrorFourHundredFour v-if="!posts && !fetching && !stale" />
    </div>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { CombinedError } from '@urql/vue'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useMeta } from '@/composables/app/useMeta'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import type { BlogMenuFragment, BlogPostPreviewFragment } from '@/graphql/api'
import { useRoutes } from '@/router/routes'
import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'

import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import BlogGrid from '@/components/cms/component/blog/BlogGrid.vue'
import BlogHeader from '@/components/cms/component/blog/BlogHeader.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import PageErrorFourHundredFour from '@/components/PageErrorFourHundredFour.vue'
import TransitionListGrid from '@/transitions/TransitionListGrid.vue'
import { usePageCount } from '@/composables/page-count.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'

const props = defineProps<{
    page: number
    categories?: BlogMenuFragment[]
    posts: BlogPostPreviewFragment[] | null | undefined
    totalPosts: number | null | undefined
    hasData: boolean
    title?: string
    metaTitle?: string
    description?: string
    breadcrumbTitle?: string
    fetching: boolean
    stale: boolean
    error: CombinedError | undefined
    responseErrors: ErrorCollectionWithTraces
}>()

defineEmits<{
    'try-again': []
}>()

const { t } = useI18n()
const router = useRouter()
const routes = useRoutes()

const breadcrumbs = computed((): Breadcrumb[] => {
    // TODO translate
    const breadcrumbs: Breadcrumb[] = [
        {
            title: 'Home',
            to: routes.home(),
        },
        {
            title: 'Blog',
            to: routes.blog.list(),
        },
    ]

    if (props.breadcrumbTitle) {
        breadcrumbs.push({
            title: props.breadcrumbTitle,
        })
    }

    return breadcrumbs
})

useMeta(
    {
        title: () => props.metaTitle,
        description: () => props.description,
    },
    toRef(() => props.posts),
    toRef(() => props.fetching),
    toRef(() => props.stale)
)

const route = useRoute()

const getPageLink = (page: number): ButtonRouteRelative => ({
    name: route.name || undefined,
    params: {
        ...route.params,
        page: page === 1 ? undefined : page,
    },
})

const classBlogContent = computed(() => ({
    fetching: props.fetching,
}))

const limit = 10

const pageCount = usePageCount(
    () => props.totalPosts ?? 0,
    () => limit
)
const pageWatcher = usePageWatcher(
    () => props.page,
    pageCount,
    () => {
        return router.resolve(getPageLink(1)).fullPath
    },
    302
)

await pageWatcher()
</script>

<style lang="scss" scoped>
.blog {
    .blog-content {
        @include fetching;
    }
}
</style>
